/* eslint-disable prettier/prettier */
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import CommonLollipopChart from "../../chart/lollipop/CommonLollipopChart";
import InvertedColumnChart from "../../chart/column/InvertedColumnChart";
import {
  getV5ReportSummary,
  usergetInfo,
  getV5MetricHistory,
} from "../../../services/romReports";
import {
  getParams,
  getMetricReading,
  formatReportGender,
} from "../../../utils/helpers";
import { pageLocale } from "../../../utils/locale";
import {
  attachSeverity,
  severityForType,
  dateFormater,
} from "../../../utils/commonfunction";
import { forHistoryScoreShow } from "../../../utils/pdfGraphConstants";
import { METRICS_CONFIG, getTugCategoryConfig } from "../../../utils/metricsConfig";
import {
  getPdfPagePlan,
  getSelectedExercise,
  getSelectedPages,
  metricText,
  normalizeSeverity,
  pageNumberFor,
  parseSelectedExercises,
  truncateList,
  truncateText,
} from "../../../utils/pdfReportHelpers";
import { setLoading } from "../../state/action/globalAction";
import "./tugPdfReport.scss";

// ── severity helpers ──────────────────────────────────────────────────────────
const SEVERITY_COLOR = { good: "#10b981", average: "#f59e0b", poor: "#ef4444" };
const DOMAIN_COLOR = {
  trans: "#3b82f6",
  gait: "#059669",
  bal: "#7c3aed",
  turn: "#d97706",
  pwr: "#dc2626",
  ctrl: "#0891b2",
};

const CAT_DOMAIN_COLOR = {
  functional_transitions: "#3b82f6",
  gait_performance: "#059669",
  frontal_balance_symmetry: "#7c3aed",
  balance_postural_control: "#7c3aed",
  turning: "#d97706",
  turning_joint_mechanics: "#d97706",
  power: "#dc2626",
  control: "#0891b2",
};

const HISTORY_PAGE_GROUPS = {
  front: [
    ["functional_transitions"],
    ["gait_performance"],
    ["frontal_balance_symmetry", "turning"],
    ["power"],
  ],
  side: [
    ["functional_transitions"],
    ["gait_performance"],
    ["balance_postural_control", "turning_joint_mechanics"],
    ["power", "control"],
  ],
};

const chunk = (arr, size) =>
  Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
    arr.slice(i * size, i * size + size),
  );

const fmtUnit = (unit) =>
  ({
    seconds: "s",
    centimeters: "cm",
    meters: "m",
    "cm/s": "cm/s",
    "steps/min": "spm",
    "percentage (%)": "%",
    "%": "%",
    "degrees (°)": "°",
    "deg/s": "deg/s",
    "m/s": "m/s",
    count: "count",
    ms: "ms",
    unitless: "",
  }[unit] ?? unit);

const getSeverityFromMetric = (raw) => {
  if (!raw) return null;
  const range = attachSeverity(raw.range);
  return severityForType(raw.type, range) || normalizeSeverity(raw.type);
};

const severityLabel = (s, mv2) => {
  if (s === "good") return mv2?.status_good || "Good";
  if (s === "average") return mv2?.status_moderate || "Average";
  if (s === "poor") return mv2?.status_poor || "Poor";
  return "N/A";
};

const trendArrow = (trend) => {
  if (trend === "improving") return { arrow: "↑", color: "#10b981" };
  if (trend === "declining") return { arrow: "↓", color: "#ef4444" };
  return { arrow: "—", color: "#94a3b8" };
};

const formatBandText = (band, idx, allBands, unit) => {
  const lo = Number(band?.minMax?.[0]);
  const hi = Number(band?.minMax?.[1]);
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return "";
  const lowerBetter = Number(allBands?.[0]?.minMax?.[0]) === 0;
  const isFirst = idx === 0;
  const isLast = idx === (allBands?.length || 0) - 1;
  const u = unit || "";
  const fmt = (n) => (n % 1 === 0 ? n : Number(n.toFixed(1)));
  if (lo === hi) return `${fmt(lo)}${u}`;
  if (lowerBetter) {
    if (isFirst) return `≤ ${fmt(hi)}${u}`;
    if (isLast) return `> ${fmt(lo)}${u}`;
    return `${fmt(lo)}-${fmt(hi)}${u}`;
  }
  if (isFirst) return `≥ ${fmt(lo)}${u}`;
  if (isLast) return `< ${fmt(hi)}${u}`;
  return `${fmt(lo)}-${fmt(hi)}${u}`;
};

const fmtVal = (v, unit) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  if (unit === "steps/min" || unit === "count") return String(Math.round(n));
  if (unit === "percentage (%)") return n % 1 === 0 ? String(n) : n.toFixed(1);
  if (unit === "unitless") return n % 1 === 0 ? String(n) : n.toFixed(2);
  return n % 1 === 0 ? String(n) : n.toFixed(1);
};

// ── Metric history chart sub-component ───────────────────────────────────────
const MetricChart = ({
  title,
  type,
  filterField,
  graphRef,
  fallRisk,
  color,
  latestVal,
  latestUnit,
  latestSeverity,
  chartType = "lollipop",
  range,
}) => {
  const [histData, setHistData] = useState(null);
  const mv2 = pageLocale("metricsv2");

  const bands =
    Array.isArray(range) && range.length > 0 ? attachSeverity(range) : null;

  useEffect(() => {
    let mounted = true;
    getV5MetricHistory(type, filterField, 5)
      .then((res) => {
        if (!mounted) return;
        const data = res.data?.data?.data || [];
        const cfg = forHistoryScoreShow[graphRef] || {};
        setHistData({ data, min: cfg.min, max: cfg.max });
      })
      .catch(() => {
        if (mounted) setHistData({ data: [] });
      });
    return () => {
      mounted = false;
    };
  }, [type, filterField, graphRef]);

  const tierColor = SEVERITY_COLOR[latestSeverity] || "#94a3b8";

  return (
    <div className="ch-card">
      <div className="ch-hdr" style={{ borderLeftColor: color }}>
        <span className="ch-title">{title}</span>
        <div className="ch-latest-wrap">
          {latestVal !== "—" && (
            <>
              <span className="ch-latest-val" style={{ color: tierColor }}>
                {latestVal}
              </span>
              <span className="ch-latest-unit">{latestUnit}</span>
              {latestSeverity && (
                <span className="ch-tier" style={{ background: tierColor }}>
                  {severityLabel(latestSeverity, mv2)}
                </span>
              )}
            </>
          )}
        </div>
      </div>
      <div className="ch-card-body">
        <div className="ch-side">
          {bands && bands.length > 0 && (
            <div className="ch-bands-legend">
              {bands.map((b, i) => (
                <div
                  key={i}
                  className={`ch-band-row ch-band-${b.severity || "avg"}`}
                >
                  <span className="ch-band-lbl">
                    {b.severity === "good"
                      ? severityLabel("good", mv2)
                      : b.severity === "poor"
                      ? severityLabel("poor", mv2)
                      : severityLabel("average", mv2)}
                    :
                  </span>
                  <span className="ch-band-val">
                    {formatBandText(b, i, bands, latestUnit)}
                  </span>
                </div>
              ))}
            </div>
          )}
          <div className="ch-fall-lbl">
            {mv2?.fall_risk_relevance || "Fall Risk Relevance"}
          </div>
          <div className="ch-fall">{fallRisk}</div>
        </div>
        <div className="ch-body">
          {histData && histData.data.length > 0 ? (
            chartType === "column" ? (
              <InvertedColumnChart
                historyData={histData.data}
                graphOptions={forHistoryScoreShow[graphRef] || {}}
                forPDF={true}
              />
            ) : (
              <CommonLollipopChart
                historyData={histData}
                graphOptions={forHistoryScoreShow[graphRef] || {}}
                forPDF={true}
                criteriaData={forHistoryScoreShow[graphRef]?.range}
                hidePlotLines={!graphRef}
                bands={bands}
              />
            )
          ) : (
            <span style={{ fontSize: "9px", color: "#94a3b8" }}>
              {histData
                ? mv2?.no_history_data_available || "No history data available"
                : mv2?.loading || "Loading..."}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Exercise card ─────────────────────────────────────────────────────────────
const CATEGORY_COLORS = {
  "Strength Training": "#1d4ed8",
  "Endurance Activity": "#047857",
  "Flexibility Exercises": "#6d28d9",
  "Balance Exercises": "#b45309",
};
const FALLBACK_COLORS = ["#1d4ed8", "#047857", "#6d28d9", "#b45309"];

const ExerciseCard = ({ recommendation, selectedExercise, index }) => {
  const categoryName = recommendation?.exerciseCategory || "—";
  const exercise = selectedExercise;
  const topNote = recommendation?.note;
  const color = CATEGORY_COLORS[categoryName] || FALLBACK_COLORS[index % 4];
  const hasExercise =
    exercise &&
    typeof exercise === "object" &&
    Object.keys(exercise).length > 0;

  if (!hasExercise) {
    return (
      <div className="ex-card">
        <div className="ex-cat-hdr" style={{ background: color }}>
          <div className="ex-cat-name">{categoryName}</div>
        </div>
        <div className="ex-body">
          <div className="ex-consult">
            <svg
              width="56"
              height="56"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#cbd5e1"
              strokeWidth="1.4"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v4M12 16h.01" />
            </svg>
            <div className="ex-consult-txt">
              {truncateText(
                topNote || pageLocale("newreport")?.no_exercises_available,
                130,
              )}
            </div>
            <div className="ex-consult-sub">
              {pageLocale("newreport")?.consult_care_provider}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const ignoredAdvises = ["generally safe", "一般的に安全", "一般安全"];
  const cautions = (exercise.caution || []).filter(
    (c) => c.advice && !ignoredAdvises.includes(c.advice.trim().toLowerCase()),
  );

  return (
    <div className="ex-card">
      <div className="ex-cat-hdr" style={{ background: color }}>
        <div className="ex-cat-name">{categoryName}</div>
      </div>
      <div className="ex-body">
        {exercise.aws_url && (
          <div className="ex-img">
            <img src={exercise.aws_url} alt={exercise.name} />
          </div>
        )}
        {exercise.name && (
          <div className="ex-name">{truncateText(exercise.name, 44)}</div>
        )}
        {exercise.how_to_do_it && (
          <div className="ex-desc">
            {truncateText(exercise.how_to_do_it, 150)}
          </div>
        )}
        {exercise.frequency_and_duration && (
          <div className="ex-dl">
            <span className="ex-dl-lbl">
              {pageLocale("newreport")?.frequency_duration}
            </span>
            <span className="ex-dl-val">
              {truncateText(exercise.frequency_and_duration, 82)}
            </span>
          </div>
        )}
        {exercise.note && (
          <div className="ex-safety">
            <span className="ex-safety-lbl">
              {pageLocale("newreport")?.just_note}{" "}
            </span>
            <span>{truncateText(exercise.note, 105)}</span>
          </div>
        )}
        {exercise.caution && exercise.caution.length > 0 && (
          <div className="ex-dl" style={{ marginTop: "4px" }}>
            <span className="ex-dl-lbl">
              {pageLocale("newreport")?.safety_considerations}
            </span>
            <ul
              style={{
                margin: "2px 0 0 16px",
                padding: 0,
                color: "var(--g500)",
                lineHeight: 1.45,
              }}
            >
              {truncateList(cautions, 4).map((c, i) => (
                <li key={i}>
                  <strong>{c.condition}:</strong> {c.advice}
                </li>
              ))}
              {cautions.length > 4 && <li>...</li>}
              {cautions.length === 0 && (
                <li>{pageLocale("newreport")?.consult_care_provider}</li>
              )}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const TugPdfReport = ({ reportType = "front" }) => {
  const dispatch = useDispatch();
  const [reportData, setReportData] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const { locale, pages, selectedExercises } = getParams();
  const isJapaneseLocale = String(locale || "").toLowerCase().startsWith("ja");
  const { showSummary, showExercises, showHistory } = getSelectedPages(pages);
  const tugCategories = getTugCategoryConfig(reportType);
  const historyPageCount = HISTORY_PAGE_GROUPS[reportType]?.length ?? 4;
  const pagePlan = getPdfPagePlan({ pages, historyPageCount });
  const exerciseSelections = parseSelectedExercises(selectedExercises);
  const mv2 = pageLocale("metricsv2");
  const mv2Categories = pageLocale("metricsv2_categories");
  const mv2Metrics = pageLocale("metricsv2_metrics");
  const mv2Units = pageLocale("metricsv2_units");
  const newreport = pageLocale("newreport");
  const rompdf = pageLocale("rompdf");

  useEffect(() => {
    Promise.allSettled([getV5ReportSummary(), usergetInfo()]).then(
      ([summaryRes, userRes]) => {
        if (summaryRes.status === "fulfilled") {
          setReportData(summaryRes.value?.data?.data);
        }
        if (userRes.status === "fulfilled") {
          setUserData(userRes.value?.data?.data);
        }
        setLoaded(true);
        dispatch(setLoading(false));
      },
    );
  }, [dispatch]);

  if (!loaded) {
    return (
      <div
        style={{
          padding: "40px",
          textAlign: "center",
          fontFamily: "OpenSans, sans-serif",
        }}
      >
        Loading report…
      </div>
    );
  }

  const d = reportData;

  // ── metric helpers ──
  const getMetricRaw = (path) => {
    if (!d) return null;
    const parts = path.split(".");
    let cur = d;
    for (const p of parts) {
      cur = cur?.[p];
      if (cur === undefined) return null;
    }
    return cur;
  };

  const metricValue = (path) => getMetricReading(getMetricRaw(path));

  const metricSeverity = (path) => getSeverityFromMetric(getMetricRaw(path));
  const metricTrend = (path) => getMetricRaw(path)?.trend || "stable";
  const metricRange = (path) => getMetricRaw(path)?.range || null;

  // Domain severities come from the API `summary` rollup so web and PDF stay in sync.
  const summary = reportData?.summary || {};
  const catSeverity = {
    trans: normalizeSeverity(summary.transitions),
    gait: normalizeSeverity(summary.gait),
    bal: normalizeSeverity(summary.balance),
    turn: normalizeSeverity(summary.turning),
  };
  const overallSeverity = normalizeSeverity(summary.overall);

  // ── name helpers ──
  const patientName = (() => {
    const g = (userData?.first_name || "").trim();
    const l = (userData?.last_name || "").trim();
    if (!g && !l) return userData?.name || "—";
    if (locale === "ja" || locale === "zh") return `${l} ${g}`.trim();
    return `${g} ${l}`.trim();
  })();

  const testDate = d?.timeStamp ? dateFormater(d.timeStamp) : "—";
  const gender = formatReportGender(userData?.gender, locale);
  const exercises = d?.exerciseRecommendation || [];
  const testName =
    reportType === "side"
      ? newreport?.tug_side || "TUG (Side)"
      : newreport?.tug_front || "TUG (Front)";
  const footer = (key) => (
    <>
      Page {pageNumberFor(pagePlan, key)} of {pagePlan.length} &nbsp;·&nbsp;{" "}
      {testName} &nbsp;·&nbsp; {patientName} &nbsp;·&nbsp; {testDate}
    </>
  );

  // ── metric row renderer ──
  const MetricRow = ({ label, path, unit, isTime }) => {
    const val = metricValue(path, isTime);
    const sev = metricSeverity(path);
    const trend = metricTrend(path);
    const { arrow, color: arrowColor } = trendArrow(trend);
    const [cat, field] = path.split(".");
    const metricCfg = METRICS_CONFIG.find(
      (m) => m.category === cat && m.apiField === field,
    );
    const unitDisplay =
      (metricCfg && mv2Units?.[metricCfg.metric]) || fmtUnit(unit) || "";
    return (
      <div className="m-row">
        <span className="m-name">{label}</span>
        <span className="m-val">{fmtVal(val, unit)}</span>
        <span className="m-unit">{unitDisplay}</span>
        <span className="m-trend" style={{ color: arrowColor }}>
          {arrow}
        </span>
        {sev ? (
          <span className={`tb bg-${sev}`}>{severityLabel(sev, mv2)}</span>
        ) : (
          <span className="tb" style={{ background: "#94a3b8" }}>
            N/A
          </span>
        )}
      </div>
    );
  };

  // ── find METRICS_CONFIG for fallRisk text ──
  const getFallRisk = (metricKey) =>
    metricText(metricKey).fallRisk ||
    METRICS_CONFIG.find((m) => m.metric === metricKey)?.fallRiskRelevance ||
    "";

  return (
    <div className={`tug-pdf-root${isJapaneseLocale ? " tug-pdf-root-ja" : ""}`}>
      {/* ════ PAGE 1 – SUMMARY ════ */}
      {showSummary && (
        <div className="page">
          <div className="hdr">
            <div className="hdr-l">
              <span className="hdr-test">{testName}</span>
              <span className="hdr-pill">
                {newreport?.summary || "Summary"}
              </span>
            </div>
            <div className="hdr-logo">
              <span className="hdr-logo-pw">Powered by</span>
              <span className="hdr-logo-name">SeniorLife</span>
            </div>
          </div>

          <div className="pg-body">
            {/* Patient row */}
            <div className="pt-row">
              <div className="pt-info">
                <div className="pt-name">{patientName}</div>
                <div className="pt-grid">
                  <div>
                    <div className="pt-lbl">{newreport?.age || "Age"}</div>
                    <div className="pt-val">{userData?.age || "—"}</div>
                  </div>
                  <div>
                    <div className="pt-lbl">
                      {newreport?.gender || "Gender"}
                    </div>
                    <div className="pt-val">{gender || "—"}</div>
                  </div>
                  <div>
                    <div className="pt-lbl">
                      {newreport?.record_date || "Record Date"}
                    </div>
                    <div className="pt-val">{testDate}</div>
                  </div>
                  <div>
                    <div className="pt-lbl">
                      {newreport?.assessed_by || "Assessed By"}
                    </div>
                    <div className="pt-val">
                      {d?.assessedBy ||
                        userData?.assessed_by ||
                        userData?.staffDetails?.name ||
                        "—"}
                    </div>
                  </div>
                  <div>
                    <div className="pt-lbl">
                      {newreport?.test_person_id || "Test Person ID"}
                    </div>
                    <div className="pt-val">
                      {d?.testPersonId || userData?.test_person_id || "—"}
                    </div>
                  </div>
                  <div>
                    <div className="pt-lbl">
                      {newreport?.seniorLife_id ||
                        newreport?.seniorlife_id ||
                        "SeniorLife ID"}
                    </div>
                    <div className="pt-val">
                      {d?.seniorlifeId ||
                        userData?.seniorlife_id ||
                        userData?.seniorId ||
                        "—"}
                    </div>
                  </div>
                  <div>
                    <div className="pt-lbl">
                      {newreport?.location_id || "Location ID"}
                    </div>
                    <div className="pt-val">
                      {d?.locationId ||
                        userData?.location_id ||
                        userData?.locationId ||
                        "—"}
                    </div>
                  </div>
                  <div>
                    <div className="pt-lbl">
                      {newreport?.test_type || "Test Type"}
                    </div>
                    <div className="pt-val">{testName}</div>
                  </div>
                </div>
              </div>
              <div className="photo-frame">
                {d?.tumbnailPath ? (
                  <img src={d.tumbnailPath} alt={patientName} />
                ) : (
                  <div className="photo-ph">
                    <svg
                      width="40"
                      height="40"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    >
                      <circle cx="12" cy="8" r="4" />
                      <path d="M4 20c0-4 3.6-6 8-6s8 2 8 6" />
                    </svg>
                    <span>{newreport?.photo || "Photo"}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Risk row */}
            <div className="risk-row">
              <span className="risk-lbl">
                {mv2?.risk_level_label || "Overall Risk"}
              </span>
              <span
                className="risk-badge"
                style={{
                  background: SEVERITY_COLOR[overallSeverity] || "#94a3b8",
                }}
              >
                {(severityLabel(overallSeverity, mv2) || "N/A").toUpperCase()}
              </span>
              <span className="risk-desc">
                {d?.exerciseRecommended?.programDescription || ""}
              </span>
            </div>

            {/* Scorecard */}
            <div className="scorecard">
              {[
                {
                  key: "trans",
                  label: mv2Categories?.functional_transitions || "Transitions",
                  color: DOMAIN_COLOR.trans,
                },
                {
                  key: "gait",
                  label: mv2Categories?.gait_performance || "Gait",
                  color: DOMAIN_COLOR.gait,
                },
                {
                  key: "bal",
                  label: mv2Categories?.frontal_balance_symmetry || "Balance",
                  color: DOMAIN_COLOR.bal,
                },
                {
                  key: "turn",
                  label: mv2Categories?.turning || "Turning",
                  color: DOMAIN_COLOR.turn,
                },
                {
                  key: "pwr",
                  label: mv2Categories?.power || "Power",
                  color: DOMAIN_COLOR.pwr,
                },
                {
                  key: "ctrl",
                  label: mv2Categories?.control || "Control",
                  color: DOMAIN_COLOR.ctrl,
                },
              ].map(({ key, label }) => {
                const sev = catSeverity[key];
                return (
                  <div
                    key={key}
                    className="sc-card"
                    style={{
                      borderTopColor: sev ? SEVERITY_COLOR[sev] : "#94a3b8",
                    }}
                  >
                    <div className="sc-name">{label}</div>
                    {sev ? (
                      <span
                        className="sc-badge"
                        style={{ background: SEVERITY_COLOR[sev] }}
                      >
                        {severityLabel(sev, mv2)}
                      </span>
                    ) : (
                      <span
                        className="sc-badge"
                        style={{ background: "#94a3b8" }}
                      >
                        N/A
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Metrics columns */}
            <div className="metrics-cols">
              <div className="mc">
                {/* Transitions */}
                <div className="dm-block">
                  <div
                    className="dm-hdr"
                    style={{
                      background: DOMAIN_COLOR.trans,
                      borderLeftColor: DOMAIN_COLOR.trans,
                    }}
                  >
                    <span className="dm-name">
                      {mv2Categories?.functional_transitions || "Transitions"}
                    </span>
                    <span
                      className="dm-badge"
                      style={{
                        background:
                          SEVERITY_COLOR[catSeverity.trans] || "#94a3b8",
                      }}
                    >
                      {severityLabel(catSeverity.trans, mv2)}
                    </span>
                  </div>
                  <MetricRow
                    label={mv2Metrics?.TUG_Time || "TUG Time"}
                    path="tug.time"
                    unit="seconds"
                  />
                  <MetricRow
                    label={mv2Metrics?.Sit_to_Stand_Time || "Sit to Stand Time"}
                    path="transitions.sitToStandTime"
                    unit="seconds"
                    isTime
                  />
                  <MetricRow
                    label={mv2Metrics?.Attempts_to_Rise || "Attempts to Rise"}
                    path="transitions.attemptsToRise"
                    unit="count"
                  />
                  <MetricRow
                    label={mv2Metrics?.Stand_to_Sit_Time || "Stand to Sit Time"}
                    path="transitions.standToSitTime"
                    unit="seconds"
                    isTime
                  />
                </div>

                {/* Balance */}
                <div className="dm-block">
                  <div
                    className="dm-hdr"
                    style={{
                      background: DOMAIN_COLOR.bal,
                      borderLeftColor: DOMAIN_COLOR.bal,
                    }}
                  >
                    <span className="dm-name">
                      {mv2Categories?.frontal_balance_symmetry || "Balance"}
                    </span>
                    <span
                      className="dm-badge"
                      style={{
                        background:
                          SEVERITY_COLOR[catSeverity.bal] || "#94a3b8",
                      }}
                    >
                      {severityLabel(catSeverity.bal, mv2)}
                    </span>
                  </div>
                  {reportType === "side" ? (
                    <>
                      <MetricRow
                        label={mv2Metrics?.Foot_Clearance || "Foot Clearance"}
                        path="balance.footClearance"
                        unit="centimeters"
                      />
                      <MetricRow
                        label={mv2Metrics?.Trunk_Sway || "Trunk Sway"}
                        path="balance.trunkSway"
                        unit="degrees"
                      />
                    </>
                  ) : (
                    <>
                      <MetricRow
                        label={mv2Metrics?.Head_Wobbling || "Head Wobbling"}
                        path="balance.headWobble"
                        unit="centimeters"
                      />
                      <MetricRow
                        label={
                          mv2Metrics?.Shoulder_Balance_LRdeg ||
                          "Shoulder Balance L/R"
                        }
                        path="balance.shoulderBalance"
                        unit="degrees"
                      />
                      <MetricRow
                        label={mv2Metrics?.Hip_Balance_LRdeg || "Hip Balance L/R"}
                        path="balance.hipBalance"
                        unit="degrees"
                      />
                      <MetricRow
                        label={mv2Metrics?.Trunk_Sway || "Trunk Sway"}
                        path="balance.trunkSway"
                        unit="degrees"
                      />
                      <MetricRow
                        label={mv2Metrics?.Postural_Control || "Postural Control"}
                        path="balance.posturalControl"
                        unit="m²/H²"
                      />
                    </>
                  )}
                </div>
              </div>

              <div className="mc">
                {/* Gait */}
                <div className="dm-block">
                  <div
                    className="dm-hdr"
                    style={{
                      background: DOMAIN_COLOR.gait,
                      borderLeftColor: DOMAIN_COLOR.gait,
                    }}
                  >
                    <span className="dm-name">
                      {mv2Categories?.gait_performance || "Gait"}
                    </span>
                    <span
                      className="dm-badge"
                      style={{
                        background:
                          SEVERITY_COLOR[catSeverity.gait] || "#94a3b8",
                      }}
                    >
                      {severityLabel(catSeverity.gait, mv2)}
                    </span>
                  </div>
                  <MetricRow
                    label={mv2Metrics?.Speed || "Speed"}
                    path="gait.speed"
                    unit="cm/s"
                  />
                  <MetricRow
                    label={mv2Metrics?.Steps_per_Minute || "Steps per Minute"}
                    path="gait.cadence"
                    unit="steps/min"
                  />
                  <MetricRow
                    label={mv2Metrics?.Step_Length || "Step Length"}
                    path="gait.stepLength"
                    unit="centimeters"
                  />
                  {reportType === "side" ? (
                    <>
                      <MetricRow
                        label={
                          mv2Metrics?.Right_Left_Step_Symmetry ||
                          "R/L Step Symmetry"
                        }
                        path="gait.rightLeftStepSymmetry"
                        unit="unitless"
                      />
                      <MetricRow
                        label={mv2Metrics?.Gait_Smoothness || "Gait Smoothness"}
                        path="gait.gaitSmoothness"
                        unit="unitless"
                      />
                    </>
                  ) : (
                    <>
                      <MetricRow
                        label={
                          mv2Metrics?.Right_Left_Step_Symmetry ||
                          "R/L Step Symmetry"
                        }
                        path="gait.rightLeftStepSymmetry"
                        unit="unitless"
                      />
                      <MetricRow
                        label={mv2Metrics?.Foot_Placement || "Foot Placement"}
                        path="gait.footPlacement"
                        unit="centimeters"
                      />
                    </>
                  )}
                </div>

                {/* Turning & Joint Mechanics */}
                <div className="dm-block">
                  <div
                    className="dm-hdr"
                    style={{
                      background: DOMAIN_COLOR.turn,
                      borderLeftColor: DOMAIN_COLOR.turn,
                    }}
                  >
                    <span className="dm-name">
                      {mv2Categories?.turning_joint_mechanics ||
                        mv2Categories?.turning ||
                        "Turning & Joint Mechanics"}
                    </span>
                    <span
                      className="dm-badge"
                      style={{
                        background:
                          SEVERITY_COLOR[catSeverity.turn] || "#94a3b8",
                      }}
                    >
                      {severityLabel(catSeverity.turn, mv2)}
                    </span>
                  </div>
                  <MetricRow
                    label={mv2Metrics?.Turning_Time || "Turning Time"}
                    path="turning.turningTime"
                    unit="seconds"
                  />
                  <MetricRow
                    label={mv2Metrics?.Steps_During_Turn || "Steps During Turn"}
                    path="turning.stepsDuringTurn"
                    unit="count"
                  />
                  {reportType === "side" && (
                    <>
                      <MetricRow
                        label={mv2Metrics?.Joint_Angles_Knee || "Joint Angles Knee"}
                        path="turning.jointAnglesKnee"
                        unit="degrees"
                      />
                      <MetricRow
                        label={mv2Metrics?.Joint_Angles_Hip || "Joint Angles Hip"}
                        path="turning.jointAnglesHip"
                        unit="degrees"
                      />
                    </>
                  )}
                </div>

                {/* Power */}
                <div className="dm-block">
                  <div
                    className="dm-hdr"
                    style={{
                      background: DOMAIN_COLOR.pwr,
                      borderLeftColor: DOMAIN_COLOR.pwr,
                    }}
                  >
                    <span className="dm-name">
                      {mv2Categories?.power || "Power"}
                    </span>
                    <span
                      className="dm-badge"
                      style={{
                        background: SEVERITY_COLOR[catSeverity.pwr] || "#94a3b8",
                      }}
                    >
                      {severityLabel(catSeverity.pwr, mv2)}
                    </span>
                  </div>
                  <MetricRow
                    label={mv2Metrics?.Change_Velocity || "Change Velocity"}
                    path="power.changeVelocity"
                    unit="deg/s"
                  />
                  {reportType === "front" && (
                    <MetricRow
                      label={mv2Metrics?.Lateral_Latency || "Lateral Latency"}
                      path="power.lateralLatency"
                      unit="ms"
                    />
                  )}
                </div>

                {/* Control */}
                {reportType === "side" && (
                  <div className="dm-block">
                    <div
                      className="dm-hdr"
                      style={{
                        background: DOMAIN_COLOR.ctrl,
                        borderLeftColor: DOMAIN_COLOR.ctrl,
                      }}
                    >
                      <span className="dm-name">
                        {mv2Categories?.control || "Control"}
                      </span>
                      <span
                        className="dm-badge"
                        style={{
                          background: SEVERITY_COLOR[catSeverity.ctrl] || "#94a3b8",
                        }}
                      >
                        {severityLabel(catSeverity.ctrl, mv2)}
                      </span>
                    </div>
                    <MetricRow
                      label={mv2Metrics?.Rise_Velocity || "Rise Velocity"}
                      path="control.riseVelocity"
                      unit="m/s"
                    />
                    <MetricRow
                      label={mv2Metrics?.Joint_Excursion || "Joint Excursion"}
                      path="control.jointExcursion"
                      unit="percentage (%)"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Remarks */}
            <div className="remarks-row">
              <div className="remark-box">
                <span className="remark-lbl">
                  {newreport?.staff_remarks || "Staff Observation"}
                </span>
                <span className="remark-val">
                  {d?.comments?.staffObservation || ""}
                </span>
              </div>
              <div className="remark-box">
                <span className="remark-lbl">
                  {newreport?.test_person_remarks || "Patient Remarks"}
                </span>
                <span className="remark-val">
                  {d?.comments?.patientsRemarks || ""}
                </span>
              </div>
            </div>

            <div className="disclaimer">
              <div className="disclaimer-title">Disclaimer</div>
              {newreport?.disclaimer ||
                "This report is intended for informational purposes and should not replace professional medical advice. Consult a qualified healthcare provider for diagnosis or treatment."}
            </div>
          </div>

          <div className="ftr">
            <div className="ftr-pg">{footer("summary")}</div>
          </div>
        </div>
      )}

      {/* ════ PAGE 2 – EXERCISE RECOMMENDATIONS ════ */}
      {showExercises && (
        <div className="page">
          <div className="hdr">
            <div className="hdr-l">
              <span className="hdr-test">{testName}</span>
              <span className="hdr-pill">
                {newreport?.exercise_recommendation ||
                  "Exercise Recommendations"}
              </span>
            </div>
            <div className="hdr-logo">
              <span className="hdr-logo-pw">Powered by</span>
              <span className="hdr-logo-name">SeniorLife</span>
            </div>
          </div>
          <div className="pg-body">
            <div className="ex-grid">
              {[0, 1, 2, 3].map((i) => (
                <ExerciseCard
                  key={i}
                  recommendation={exercises[i] || null}
                  selectedExercise={getSelectedExercise(
                    exercises[i],
                    i,
                    exerciseSelections,
                  )}
                  index={i}
                />
              ))}
            </div>
          </div>
          <div className="ftr">
            <div className="ftr-pg">{footer("exercises")}</div>
          </div>
        </div>
      )}

      {/* ════ HISTORY PAGES ════ */}
      {showHistory && HISTORY_PAGE_GROUPS[reportType].map((categoryKeys, pageIdx) => {
        const pageCats = tugCategories.filter((c) => categoryKeys.includes(c.key));
        return (
          <div className="page" key={pageIdx}>
            <div className="hdr">
              <div className="hdr-l">
                <span className="hdr-test">{testName}</span>
                <span className="hdr-pill">
                  {rompdf?.test_history || "Test History"}
                </span>
              </div>
              <div className="hdr-logo">
                <span className="hdr-logo-pw">Powered by</span>
                <span className="hdr-logo-name">SeniorLife</span>
              </div>
            </div>
            <div className="pg-body">
              <div className="hist-body">
                {pageCats.map((cat) => (
                  <div className="hist-sec" key={cat.key}>
                    <div className="hist-domain-row">
                      <span
                        className="hist-domain-lbl"
                        style={{ color: CAT_DOMAIN_COLOR[cat.key] }}
                      >
                        {mv2Categories?.[cat.key] || cat.key}
                      </span>
                      <div
                        className="hist-domain-line"
                        style={{ background: CAT_DOMAIN_COLOR[cat.key] }}
                      />
                    </div>
                    {chunk(cat.metrics, 2).map((pair, rowIdx) => (
                      <div className="charts-row" key={rowIdx}>
                        {pair.map((metric) => (
                          <MetricChart
                            key={metric.metric}
                            title={metricText(metric.metric).label}
                            type={metric.category}
                            filterField={metric.apiField}
                            graphRef={metric.graphReference}
                            fallRisk={getFallRisk(metric.metric)}
                            color={CAT_DOMAIN_COLOR[cat.key]}
                            latestVal={fmtVal(
                              metricValue(`${metric.category}.${metric.apiField}`),
                              metric.unit,
                            )}
                            latestUnit={fmtUnit(metric.unit)}
                            latestSeverity={metricSeverity(`${metric.category}.${metric.apiField}`)}
                            range={metricRange(`${metric.category}.${metric.apiField}`)}
                          />
                        ))}
                        {pair.length === 1 && <div />}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
            <div className="ftr">
              <div className="ftr-pg">{footer(`history-${pageIdx}`)}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TugPdfReport;
